// ─────────────────────────────────────────────────────────────────────────────
// ScoreLM super-admin portal: API CONTRACT TYPES
//
// The request and response shapes of /api/v1/admin/auth/* and
// /api/v1/admin/platform/* that the portal uses.
//
// This file is the single source of truth for the contract. The prose
// version, with status codes, validation rules and ordering guarantees, is
// admin-portal/API_CONTRACT.md. Keep the two in sync.
//
// Wire format: JSON, snake_case keys, ISO-8601 UTC timestamps.
// ─────────────────────────────────────────────────────────────────────────────

/** A known string union that still accepts other strings (legacy values). */
export type OpenString = string & Record<never, never>

/** ISO-8601 timestamp in UTC, e.g. "2026-10-05T14:03:11.000Z". */
export type ISODateTime = string
/** Calendar date, e.g. "2026-10-05". Used for inclusive date filters. */
export type ISODate = string
/** Sport pack id, e.g. "basketball", "soccer". */
export type SportId = string

// ─── Errors ─────────────────────────────────────────────────────────────────

/**
 * Error envelope for the platform routes' non-2xx responses. Fastify's own
 * errors are `{ error: "Bad Request", message }`; the client normalizes both.
 */
export interface ApiErrorBody {
  error: {
    code: ApiErrorCode
    /** A sentence a super-admin can act on. Shown in the UI as-is. */
    message: string
    /** For validation errors: the request field at fault, e.g. "subdomain". */
    field?: string
    details?: Record<string, unknown>
  }
}

export type ApiErrorCode =
  // 400 / 422
  | 'bad_request'
  | 'validation_failed'
  | 'subdomain_invalid'
  | 'subdomain_reserved'
  | 'domain_invalid'
  | 'confirmation_mismatch'
  | 'reason_required'
  // 401 / 403
  | 'unauthorized'
  | 'forbidden'
  | 'not_super_admin'
  // 404 / 409
  | 'not_found'
  | 'conflict'
  | 'subdomain_taken'
  | 'domain_taken'
  | 'addon_required'
  | 'last_super_admin'
  | 'last_sport'
  | 'default_sport'
  // 429 / 5xx / external
  | 'rate_limited'
  | 'cloudflare_error'
  | 'stripe_not_configured'
  | 'server_error'
  // Staff invites and password reset (registration plan S1/S2)
  | 'invite_not_found'
  | 'invite_used'
  | 'invite_expired'
  | 'invite_revoked'
  | 'invite_pending'
  | 'invite_not_pending'
  | 'already_member'
  | 'already_super_admin'
  | 'account_exists'
  | 'account_inactive'
  | 'wrong_account'
  | 'resend_limit'
  | 'no_platform_address'
  | 'impersonation_forbidden'
  // Client-side only (never sent by the server)
  | 'network_error'

// ─── Auth ───────────────────────────────────────────────────────────────────

export type LeagueRole = 'admin' | 'editor' | 'stat-keeper'
export type PlatformRole = 'super_admin'

export interface Me {
  id: number
  email: string
  name: string | null
  /** Legacy global role column ("admin"). Not used for authorization here. */
  role: string
  platform_role: PlatformRole | null
  leagues: Array<{ league_id: number; role: LeagueRole }>
}

/** POST /admin/auth/login */
export interface LoginRequest {
  email: string
  password: string
  /**
   * Optional. When "platform", the server refuses to issue a token to
   * a user who isn't a super-admin (403 `not_super_admin`), instead of
   * issuing one that the portal then throws away.
   */
  portal?: 'platform'
}

export interface LoginResponse {
  token: string
  user: Me
}

// ─── Staff invites and password reset (registration plan S1/S2) ─────────────

/** A staff invite as the platform invite routes return it. `role` is 'super_admin' for a super-admin invite. */
export interface StaffInvite {
  id: number
  email: string
  role: LeagueRole | 'super_admin'
  /** null for a super-admin invite. */
  league_id: number | null
  status: 'pending' | 'expired' | 'accepted' | 'revoked'
  expires_at: ISODateTime
  send_count: number
  last_sent_at: ISODateTime | null
  /** 'email_not_configured', 'email_undeliverable' (a reserved or test domain,
   *  never mailed), 'no_platform_address' or a provider error; null after a good send. */
  last_send_error: string | null
  invited_by: { id: number; name: string | null; email: string } | null
  created_at: ISODateTime
}

export type InviteDelivery = 'sent' | 'not_configured' | 'undeliverable' | 'failed'

/** POST …/invites, POST /platform/invites/:id/resend. `link` is a credential: shown, never stored. */
export interface InviteSendResult {
  invite: StaffInvite
  delivery: InviteDelivery
  /** null when the league has no ScoreLM address to link to. */
  link: string | null
}

/** GET /platform/invites/:id/link */
export interface InviteLink {
  url: string
  expires_at: ISODateTime
}

/** GET /admin/auth/invite?token=: never says whether the address has an account. */
export interface InviteLookup {
  kind: 'league' | 'super_admin'
  email: string
  role: string
  league: { id: number; name: string; logo_url: string | null } | null
  invited_by: { name: string } | null
  expires_at: ISODateTime
}

/** POST /admin/auth/invite/accept (201) */
export interface AcceptInviteResponse {
  token: string
  user: Me
  invite: { kind: 'league' | 'super_admin'; league_id: number | null }
}

/** POST /admin/auth/invite/claim */
export interface ClaimInviteResponse {
  user: Me | null
  already_member: boolean
}

// ─── The 6-add-on model ─────────────────────────────────────────────────────

/**
 * Layer 1: one account status per tenant (super-admin only).
 * - trialing: within the trial window. Live while trial_ends_at is in the future.
 * - active:   paying, or manually activated. Live.
 * - past_due: Stripe payment failed. Still live (grace period).
 * - suspended: locked by a super-admin (replaces the old leagues.is_active).
 *   Every public route, Fan AI included, is down. Requires a reason.
 * - canceled: no longer a customer (also covers today's "expired" and "none").
 */
export const ACCOUNT_STATUSES = ['trialing', 'active', 'past_due', 'suspended', 'canceled'] as const
export type AccountStatus = (typeof ACCOUNT_STATUSES)[number]

/** Layer 2: the six add-ons. Everything else is core (always on).
 *  `registration` is in development and in no package: off for every league
 *  until a super admin switches it on. */
export const ADDON_KEYS = ['scorebook', 'broadcast', 'fan_ai', 'ticketing', 'white_label', 'registration'] as const
export type AddonKey = (typeof ADDON_KEYS)[number]
export type AddonMap = Record<AddonKey, boolean>

/** One optional package. NULL means "add-ons only". */
export type PackageKey = 'standard'

/** Where billing comes from. Derived: stripe_customer_id set → stripe. */
export type BillingSource = 'stripe' | 'manual' | 'none'

export interface Suspension {
  reason: string
  suspended_at: ISODateTime
  suspended_by: UserRef | null
}

export interface Account {
  status: AccountStatus
  /** Server-computed: may the public site serve traffic right now? */
  is_live: boolean
  trial_ends_at: ISODateTime | null
  current_period_end: ISODateTime | null
  /** "standard", or null for add-ons-only tenants. Legacy rows may say "early_access". */
  package_key: PackageKey | OpenString | null
  package_name: string | null
  billing_source: BillingSource
  stripe_customer_id: string | null
  stripe_subscription_id: string | null
  notes: string | null
  /** Set while status = suspended. */
  suspension: Suspension | null
  updated_at: ISODateTime | null
  /**
   * Transitional: the pre-rewrite status ("none" | "expired" | …) while the
   * backend still stores it. Lets the UI say "No subscription" rather than a
   * bare "Canceled". Drop once the migration has run.
   */
  legacy_status?: string | null
}

// ─── Attribution (stored choice, in effect only with White-label) ───────────

export const ATTRIBUTION_KEYS = ['footer', 'share_cards', 'corner_badge'] as const
export type AttributionKey = (typeof ATTRIBUTION_KEYS)[number]

export interface AttributionSetting {
  /** What the public site does now: (override ?? derived) while White-label is in effect (on and the account live), else derived. */
  effective: boolean
  /** The launch default: footer shown, share cards and corner badge hidden. */
  derived: boolean
  /** The stored choice; null = not stored (uses the launch default). Only applies while White-label is in effect. */
  override: boolean | null
}
export type Attribution = Record<AttributionKey, AttributionSetting>

// ─── Fees ───────────────────────────────────────────────────────────────────

export type FeeType = 'percentage' | 'flat'
export type RegistrationFeeMode = 'passed_on' | 'absorbed'

export interface FeeSetting {
  type: FeeType
  /** percentage: 0–100 (decimals allowed, e.g. 7.5). flat: integer CENTS. */
  value: number
  /** true when no override is stored and the platform default applies. */
  is_default: boolean
}

export interface Fees {
  ticketing: FeeSetting
  registration: FeeSetting & { mode: RegistrationFeeMode }
  platform_defaults: {
    ticketing: Omit<FeeSetting, 'is_default'>
    registration: Omit<FeeSetting, 'is_default'> & { mode: RegistrationFeeMode }
  }
}

// ─── Domains ────────────────────────────────────────────────────────────────

export type CustomDomainState =
  | 'none' // no custom domain
  | 'pending' // registered, waiting on the customer's CNAME / certificate
  | 'active' // verified and serving
  | 'error' // Cloudflare reported a validation error
  | 'unregistered' // stored but not registered on Pages (Cloudflare off or failed)

export interface Domains {
  subdomain: string
  /** e.g. "ironbridge.scorelm.com" */
  subdomain_host: string
  /**
   * Where the subdomain is served in THIS environment (#623): "https://ironbridge.scorelm.com" in
   * production, "http://ironbridge.localhost:4810" on a local stack. Older servers don't send it.
   */
  subdomain_url?: string
  custom_domain: string | null
  custom_domain_state: CustomDomainState
  custom_domain_verified_at: ISODateTime | null
  redirect_to_custom: boolean
  /** The CNAME target the customer must point at, e.g. "app.scorelm.com". */
  cname_target: string | null
}

/** GET /platform/leagues/:id/custom-domain/status (exists; unchanged). */
export interface CustomDomainStatus {
  configured: boolean
  hostname: string | null
  cf: null | {
    id: string
    hostname: string
    status: string
    ssl: {
      status?: string
      validation_errors?: Array<{ message: string }>
      validation_records?: Array<{ status: string; txt_name?: string; txt_value?: string; http_url?: string; http_body?: string }>
    }
    ownership_verification?: { type: string; name?: string; value?: string }
    verification_errors?: string[]
  }
  fallback_origin: string | null
  message?: string
}

// ─── Sports ─────────────────────────────────────────────────────────────────

/** GET /platform/leagues/:id/sports (exists; unchanged). */
export interface TenantSport {
  sport_id: SportId
  display_name: string
  is_enabled: boolean
  is_registered: boolean
  activated_at: ISODateTime | null
  activated_by: number | null
  activated_by_name: string | null
  activated_by_email: string | null
  archived_at: ISODateTime | null
  archived_by: number | null
  bootstrap_state: Record<string, boolean>
}

export interface SportBootstrapResult {
  sport_id: SportId
  league_id: number
  ran_steps: string[]
  skipped_steps: string[]
}

// ─── Tenants ────────────────────────────────────────────────────────────────

export interface UserRef {
  id: number
  email: string
  name: string | null
}

export interface TenantRef {
  id: number
  name: string
  subdomain: string
}

/** One row of the Tenants list. */
export interface TenantSummary {
  id: number
  name: string
  subdomain: string
  custom_domain: string | null
  custom_domain_verified_at: ISODateTime | null
  default_sport_id: SportId
  logo_url: string | null
  created_at: ISODateTime
  account: Account
  addons: AddonMap
  /** Number of league admins; null while the backend doesn't send it. */
  admin_count: number | null
  /** Last tenant-admin write or login (super-admin activity excluded). */
  last_activity_at: ISODateTime | null
  /** #1387: 'self_serve' from the public signup, 'portal' otherwise. Optional: older backends omit it. */
  source?: TenantSource
}

export type TenantSource = 'self_serve' | 'portal'
export const TENANT_SOURCES: readonly TenantSource[] = ['self_serve', 'portal']

/** GET /platform/leagues/:id. One consistent shape, returned by every write too. */
export interface TenantDetail extends TenantSummary {
  description: string | null
  domains: Domains
  attribution: Attribution
  fees: Fees
  /** Enabled and archived sport packs. */
  sports: TenantSport[]
  /** Small counts for the Overview tab. */
  counts: { seasons: number; teams: number; players: number; games: number } | null
  updated_at: ISODateTime | null
  /**
   * OPTIONAL (proposed). The add-ons the tenant's package grants by itself,
   * so "Reset to package default" sends exactly these. null = no package.
   * Until the server sends it, the portal uses PACKAGE_ADDON_DEFAULTS in
   * src/lib/addons.ts.
   */
  package_addons?: AddonMap | null
  /**
   * The website template fans see (resolved: a beta template while the
   * backend's SITE_TEMPLATES_BETA is off reads as Classic). Read-only here;
   * support changes it through the league admin (Website › Design).
   * Absent from older backends and from list-built details.
   */
  site_template?: { id: string; name: string }
}

export type TenantSort = 'name' | '-created_at' | 'created_at' | 'renewal' | '-last_activity_at'

/** GET /platform/leagues?… (server-side search, filter, sort and paging). */
export interface TenantListParams {
  /** Matches name, subdomain and custom domain (case-insensitive, substring). */
  q?: string
  status?: AccountStatus
  package_key?: PackageKey | 'none'
  addon?: AddonKey
  /** #1387: where the league came from ("Self-serve trials" = self_serve + trialing). */
  source?: TenantSource
  sort?: TenantSort
  /** 1-based. */
  page?: number
  /** Default 25, max 100. */
  page_size?: number
}

export interface TenantListResponse {
  leagues: TenantSummary[]
  total: number
  page: number
  page_size: number
  /** Counts per status for the same `q` (ignoring the status filter), for the filter chips. */
  status_counts: Record<AccountStatus, number> & { all: number }
}

/** GET /platform/leagues/check-subdomain?subdomain=… */
export interface SubdomainCheck {
  /** Normalized form: trimmed, lowercased. */
  subdomain: string
  available: boolean
  reason: null | 'invalid' | 'reserved' | 'taken'
  message: string | null
}

/** POST /platform/leagues: league, sport, trial and first admin in one transaction. */
export interface CreateTenantRequest {
  name: string
  subdomain: string
  default_sport_id: SportId
  description?: string | null
  /**
   * Optional first league admin. An existing user is attached, not
   * duplicated. The portal's wizard sends `null` and invites the first admin
   * by email after the create (POST /platform/leagues/:id/invites); the field
   * stays for the route's other callers.
   */
  admin?: { email: string; name?: string | null; password?: string } | null
  /** Default 14, max 180. */
  trial_days?: number
  /** Default "standard". */
  package_key?: PackageKey | null
  /** Listed keys are pinned (they stop following the package); unlisted keys follow it. */
  addons?: Partial<AddonMap>
  notes?: string | null
}

export interface ProvisioningResult {
  /** DNS + Pages registration for the subdomain. "skipped" when Cloudflare isn't configured. */
  subdomain_dns: 'ok' | 'skipped' | 'failed'
  custom_domain?: 'ok' | 'skipped' | 'failed'
  message: string | null
}

export interface CreateTenantResponse {
  tenant: TenantDetail
  admin: (UserRef & { created: boolean }) | null
  provisioning: ProvisioningResult
}

/** PATCH /platform/leagues/:id: send ONLY the fields being changed. */
export type TenantPatch = Partial<{
  name: string
  description: string | null
  default_sport_id: SportId
  logo_url: string | null
  redirect_to_custom: boolean
}>

export interface TenantResponse {
  tenant: TenantDetail
}

export interface DomainChangeResponse {
  tenant: TenantDetail
  provisioning: ProvisioningResult
}

/** PATCH /platform/leagues/:id/account. Suspend/reinstate are separate. */
export type AccountPatch = Partial<{
  status: Exclude<AccountStatus, 'suspended'>
  trial_ends_at: ISODateTime | null
  current_period_end: ISODateTime | null
  package_key: PackageKey | null
  notes: string | null
  stripe_customer_id: string | null
  stripe_subscription_id: string | null
}>

/** PATCH /platform/leagues/:id/addons. Merges: unlisted keys are untouched; null clears the override so the key follows the package. */
export type AddonsPatch = Partial<Record<AddonKey, boolean | null>>

/** PATCH /platform/leagues/:id/attribution. null clears the override. */
export type AttributionPatch = Partial<Record<AttributionKey, boolean | null>>

/** PUT /platform/leagues/:id/fees/ticketing (returns the tenant). type null = platform default. */
export interface TicketingFeeRequest {
  type: FeeType | null
  value: number | null
}
export interface RegistrationFeeRequest extends TicketingFeeRequest {
  mode?: RegistrationFeeMode | null
}

/** DELETE /platform/leagues/:id. */
export interface DeleteTenantRequest {
  /** Must equal the tenant's current subdomain, or 400 confirmation_mismatch. */
  confirm_subdomain: string
}

/** POST /platform/leagues/:id/suspend. */
export interface SuspendRequest {
  /** Required; at least 5 characters. Stored on the account and in the audit log. */
  reason: string
}

// ─── People (tenant admins) ─────────────────────────────────────────────────

export interface TenantAdmin {
  user_id: number
  email: string
  name: string | null
  role: LeagueRole
  is_active: boolean
  added_at: ISODateTime
  last_login_at: ISODateTime | null
}

/** POST /platform/leagues/:id/admins. */
export interface AddTenantAdminRequest {
  email: string
  role: LeagueRole
  name?: string | null
  /** Required when the email has no account yet (#621); an existing account keeps its own password. */
  password?: string
}

// ─── Users ──────────────────────────────────────────────────────────────────

export type UserKind = 'super_admin' | 'league_admin' | 'orphaned' | 'inactive'

export interface PlatformUser {
  id: number
  email: string
  name: string | null
  is_active: boolean
  platform_role: PlatformRole | null
  created_at: ISODateTime
  last_login_at: ISODateTime | null
  memberships: Array<{ league_id: number; league_name: string; subdomain: string; role: LeagueRole }>
}

export interface UserListParams {
  q?: string
  kind?: UserKind
  page?: number
  page_size?: number
}

export interface UserListResponse {
  users: PlatformUser[]
  total: number
  page: number
  page_size: number
  kind_counts: Record<UserKind, number> & { all: number }
}

export type UserPatch = Partial<{
  name: string | null
  is_active: boolean
  platform_role: PlatformRole | null
}>

// ─── Audit ──────────────────────────────────────────────────────────────────

export interface AuditChange {
  /** Dotted path for nested values, e.g. "attribution.footer", "addons.ticketing". */
  field: string
  before: unknown
  after: unknown
}

export interface AuditEntry {
  id: number
  created_at: ISODateTime
  league: TenantRef | null
  actor: (UserRef & { is_super_admin: boolean }) | null
  action: 'create' | 'update' | 'delete' | OpenString
  entity_type: string | null
  entity_id: number | null
  /** One line, server-written, e.g. "Turned on Ticketing". */
  summary: string | null
  /** Field-level before/after. Empty for creates with no prior state. */
  changes: AuditChange[]
  /** Suspend / delete reason, when one was given. */
  reason: string | null
  /** Raw columns, kept for the "show JSON" view. */
  old_values: unknown
  new_values: unknown
}

export interface AuditListParams {
  league_id?: number
  actor_id?: number
  action?: string
  entity_type?: string
  /** Inclusive, in `tz`. */
  from?: ISODate
  /** Inclusive (the whole day), in `tz`. */
  to?: ISODate
  /** IANA zone used to interpret from/to. Default "UTC". */
  tz?: string
  /** Free text over summary, entity type and actor email. */
  q?: string
  /** Hide changes made by super-admins (for tenant-activity views). */
  exclude_super_admin?: boolean
  limit?: number
  cursor?: number | null
}

export interface AuditListResponse {
  entries: AuditEntry[]
  next_cursor: number | null
}

export interface LoginEvent {
  id: number
  created_at: ISODateTime
  user: UserRef | null
  email: string
  success: boolean
  failure_reason: 'no_such_user' | 'bad_password' | 'inactive' | 'not_super_admin' | OpenString | null
  ip_address: string | null
  user_agent: string | null
  /** Host the login was for, resolved to a tenant when possible. */
  host: string | null
  league: TenantRef | null
}

export interface LoginListParams {
  success?: boolean
  email?: string
  league_id?: number
  ip?: string
  from?: ISODate
  to?: ISODate
  tz?: string
  limit?: number
  cursor?: number | null
}

export interface LoginListResponse {
  entries: LoginEvent[]
  next_cursor: number | null
  /** Over the whole filtered range, not just this page. */
  summary: { total: number; succeeded: number; failed: number; unique_users: number }
}

// ─── Dashboard ──────────────────────────────────────────────────────────────

export type AttentionKind =
  | 'trial_ending' // trial ends within 7 days
  | 'trial_ended' // trialing, trial_ends_at in the past (site is down)
  | 'past_due'
  | 'suspended'
  | 'domain_pending' // custom domain not active after 24h
  | 'domain_error'
  | 'no_admin' // tenant has no league admin
  | 'no_sport' // no enabled sport pack (tenant admin 404s)
  | 'inactive' // no tenant-admin activity in 30 days
  | 'new_signup' // #1387: a self-serve signup in its first 7 days

export interface AttentionItem {
  kind: AttentionKind
  severity: 'high' | 'medium' | 'low'
  tenant: TenantRef
  /** One line, e.g. "Trial ends in 3 days". */
  message: string
  /** When the condition started, or the deadline. */
  at: ISODateTime | null
  /** The tenant tab where it gets fixed. */
  tab: TenantTab
}

export interface DashboardResponse {
  generated_at: ISODateTime
  attention: AttentionItem[]
  tenants_by_status: Record<AccountStatus, number>
  /** Super-admin activity is excluded from every number below. */
  activity: {
    active_tenants_7d: number
    tenant_logins_7d: number
    content_changes_7d: number
    fan_page_views_7d: number
    fan_page_views_14d: number
  }
  addon_adoption: Array<{
    key: AddonKey
    /** Tenants with the add-on on. */
    enabled: number
    /** Of those, tenants that actually used it in the last 30 days. */
    used_30d: number
  }>
}

// ─── Exports ────────────────────────────────────────────────────────────────

/** POST /platform/audit/export and /platform/logins/export. */
export interface ExportResponse {
  /** Short-lived signed URL; the browser downloads the CSV from it. */
  url: string
  expires_at: ISODateTime
}

/** POST /platform/leagues/:id/impersonate. */
export interface ImpersonationResponse {
  url: string
  expires_at: ISODateTime
}

// ─── Usage (EXISTS, unchanged: the old portal's observability endpoints) ───

/** GET /platform/leagues/:id/usage (EXISTS). Super-admin activity is NOT excluded here. */
export interface TenantUsage {
  league: { id: number; name: string; subdomain: string | null; is_active: boolean; created_at: ISODateTime }
  admins: Array<{
    admin_user_id: number
    name: string | null
    email: string
    role: string
    last_login_at: ISODateTime | null
    added_at: ISODateTime
  }>
  activity_feed: Array<{
    id: number
    admin_user_id: number | null
    admin_name: string | null
    action: string
    entity_type: string | null
    entity_id: number | null
    created_at: ISODateTime
  }>
  content_inventory: { teams: number; players: number; games: number; seasons: number; tournaments: number }
  onboarding: { has_teams: boolean; has_players: boolean; has_season: boolean; has_games: boolean; has_final_game: boolean }
  fan_traffic: {
    page_views_7d: number
    page_views_30d: number
    over_time: Array<{ date: ISODate; views: number }>
    by_page_type: Array<{ page_type: string; views: number }>
  }
  login_history: Array<{
    admin_user_id: number | null
    email: string
    success: boolean
    failure_reason: string | null
    ip_address: string | null
    created_at: ISODateTime
  }>
}

export type TenantHealth = 'active' | 'quiet' | 'dormant' | 'new'

/** GET /platform/usage/overview (EXISTS). Per-tenant activity for the dashboard's activity table. */
export interface UsageOverview {
  tiles: {
    total_leagues: number
    active_leagues_7d: number
    logins_today: number
    content_changes_7d: number
    fan_page_views_7d: number
  }
  leagues: Array<{
    id: number
    name: string
    subdomain: string | null
    is_active: boolean
    created_at: ISODateTime
    last_activity_at: ISODateTime | null
    health: TenantHealth
    last_login_at: ISODateTime | null
    last_change_at: ISODateTime | null
    admin_count: number
    changes_7d: number
    page_views_7d: number
    page_views_sparkline: number[]
  }>
}

/** GET /public/sports (EXISTS): every registered sport pack, for the wizard. */
export interface SportCatalogEntry {
  id: SportId
  display_name: string
}

// ─── UI routing helpers (not wire types) ────────────────────────────────────

export const TENANT_TABS = [
  'overview',
  'settings',
  'domains',
  'plan',
  'add-ons',
  'sports',
  'fees',
  'people',
  'activity',
  'danger-zone',
] as const
export type TenantTab = (typeof TENANT_TABS)[number]

/** Generic page of results for endpoints that don't need extra counts. */
export interface Page<T> {
  items: T[]
  total: number
  page: number
  page_size: number
}
